/**
 * match-send.ts — every message to the match goes out here, or through the
 * command gate (domains/command-gate.ts), which keeps one state-changing
 * command on its way at a time and asks here where to send it.
 *
 * Nothing goes while there is no socket or no match, or while the game
 * reconnects: the socket is closed then, or new and not back in the match
 * until its join's snapshot has landed, and the server ignores what such a
 * socket sends (nakamaService.isRejoining). A sender hears whether its
 * message went — sendToMatch resolves false when it did not — so a vote cast
 * while the game reconnects is not shown as cast. The reconnecting banner
 * says why nothing goes; a socket refusing a message is said in the console.
 */

import type { Socket } from '@heroiclabs/nakama-js';
import { useMatchStore } from '../../domains/match';
import { nakamaService } from './nakama-service';

/** Where a message to the match goes now: the socket and the match, or null while nothing can be sent. */
export function matchConnection(): { socket: Socket; matchId: string } | null {
  const socket = nakamaService.getSocket();
  const matchId = useMatchStore.getState().matchId;
  if (!socket || !matchId || nakamaService.isRejoining()) return null;
  return { socket, matchId };
}

/**
 * Sends `message` to the match, as JSON, under `opCode`. Resolves true once
 * the socket has taken it, and false when it could not go — nowhere to send
 * it (matchConnection), or the socket refusing it, which the console says,
 * naming `what`. Never rejects.
 */
export async function sendToMatch(opCode: number, message: object, what: string): Promise<boolean> {
  const connection = matchConnection();
  if (!connection) return false;
  try {
    await connection.socket.sendMatchState(connection.matchId, opCode, JSON.stringify(message));
    return true;
  } catch (error) {
    console.warn(`[MatchSend] Not sent, the socket refused it: ${what}`, error);
    return false;
  }
}
